import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, FileText, ShoppingCart, ChevronDown } from "lucide-react";
import { COMPANY } from "@/lib/api";
import { OpenStatusBadge } from "@/components/OpenStatus";
import { useCart } from "@/context/CartContext";

const NAV_ITEMS = [
  { label: "Mini-pelles", to: "/mini-pelles", testid: "nav-mini-pelles" },
  { label: "Mandat d'import", to: "/mandat-import", testid: "nav-mandat" },
  { label: "Mini-pelle occasion", to: "/mini-pelles-occasion", testid: "nav-occasion" },
];

const SHOP_ITEMS = [
  { label: "Pièces détachées", to: "/pieces-detachees", testid: "nav-pieces" },
  { label: "Accessoires", to: "/accessoires", testid: "nav-accessoires" },
];

const INFO_ITEMS = [
  { label: "Contact", to: "/contact", testid: "nav-contact" },
  { label: "CGV de vente", to: "/cgv?doc=vente", testid: "nav-cgv" },
  { label: "CGV mandat d'import", to: "/cgv?doc=mandat-import", testid: "nav-cgv-mandat" },
  { label: "Mentions légales", to: "/cgv?doc=mentions-legales", testid: "nav-mentions" },
];

function NavDropdown({ label, items, testid }) {
  const { pathname } = useLocation();
  const active = items.some((n) => pathname.startsWith(n.to.split("?")[0]));
  return (
    <div className="relative group" data-testid={testid}>
      <button className={`px-1.5 xl:px-3 py-2 text-[13px] xl:text-sm font-semibold whitespace-nowrap ${active ? "text-[#2877C2]" : "text-[#072135]"} hover:text-[#2877C2] transition-colors inline-flex items-center gap-1`} data-testid={`${testid}-btn`} aria-haspopup="true">
        {label} <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180" />
      </button>
      <div className="absolute right-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-[opacity,transform] duration-150">
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[220px]" data-testid={`${testid}-menu`}>
          {items.map((n) => (
            <Link key={n.to} to={n.to} data-testid={n.testid} onClick={(e) => e.currentTarget.blur()} className="block px-4 py-2 text-sm font-semibold text-[#072135] hover:bg-[#F9F9F9] hover:text-[#2877C2] transition-colors">
              {n.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function PhoneButton({ testid, className = "" }) {
  return (
    <a href={`tel:${COMPANY.phoneTel}`} data-testid={testid} className={`inline-flex items-center gap-2 whitespace-nowrap shrink-0 bg-[#072135] text-white rounded-full pl-1.5 pr-4 py-1.5 hover:bg-[#0c2d48] transition-colors ${className}`}>
      <span className="w-8 h-8 rounded-full bg-[#D8B86A] text-[#072135] flex items-center justify-center"><Phone size={15} /></span>
      <span className="font-mono-tech text-[13px] xl:text-sm font-bold tracking-wide">{COMPANY.phone}</span>
    </a>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { count, setOpen: setCartOpen } = useCart();

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-gray-200">
      <div className="lmb-container">
        <div className="flex items-center justify-between h-16 lg:h-24">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0">
            <img src="/logo-192.webp" alt="La Minipelle Berjalienne" width="80" height="80" className="h-12 w-12 lg:h-20 lg:w-20 object-contain" />
            <div className="leading-tight hidden sm:block lg:hidden">
              <div className="font-heading font-black text-[#072135] text-base sm:text-lg tracking-tight uppercase">
                La Minipelle
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D8B86A] font-mono-tech font-bold">
                Berjalienne
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_ITEMS.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `px-1.5 xl:px-3 py-2 text-[13px] xl:text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive ? "text-[#2877C2]" : "text-[#072135] hover:text-[#2877C2]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <NavDropdown label="Pièces & Accessoires" items={SHOP_ITEMS} testid="nav-shop" />
            <NavDropdown label="Contact & CGV" items={INFO_ITEMS} testid="nav-infos" />
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
            <OpenStatusBadge />
            <PhoneButton testid="header-phone" />
            <button
              data-testid="cart-btn"
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-[#072135] hover:text-[#2877C2] transition-colors"
              aria-label="Panier"
            >
              <ShoppingCart size={22} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#D8B86A] text-[#072135] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center font-mono-tech">
                  {count}
                </span>
              )}
            </button>
            <button data-testid="header-devis-btn" onClick={() => navigate("/contact?devis=1")} className="lmb-btn-primary whitespace-nowrap !px-4 xl:!px-6">
              <FileText size={16} /> Devis<span className="hidden xl:inline"> gratuit</span>
            </button>
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-2">
            <a href={`tel:${COMPANY.phoneTel}`} data-testid="m-phone-btn" aria-label="Appeler" className="w-10 h-10 rounded-full bg-[#D8B86A] text-[#072135] flex items-center justify-center">
              <Phone size={18} />
            </a>
            <button data-testid="m-cart-btn" onClick={() => setCartOpen(true)} className="relative p-2 text-[#072135]">
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D8B86A] text-[#072135] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{count}</span>
              )}
            </button>
            <button data-testid="mobile-menu-btn" className="p-2 text-[#072135]" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain" data-testid="mobile-menu">
          <div className="lmb-container py-4 pb-8 flex flex-col gap-1">
            {[...NAV_ITEMS, ...SHOP_ITEMS, ...INFO_ITEMS].map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-3 px-2 text-[#072135] font-semibold border-b border-gray-100">
                {n.label}
              </Link>
            ))}
            <PhoneButton testid="m-menu-phone" className="mt-4 justify-center" />
            <button onClick={() => { setOpen(false); navigate("/contact?devis=1"); }} className="lmb-btn-primary mt-3">
              <FileText size={16} /> Devis gratuit
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
